import { useState } from "react";
import { CalendarDays, ChevronDown } from "lucide-react";
import { copy } from "../i18n";
import type { Locale, ScheduleResponse } from "../types";
import { BOOKING_DAYS_AHEAD, calendarLabelToIso, getBookingDateWindow, isDateInWindow, isoToCalendarLabel } from "../lib/dates";
import { formatDay } from "../lib/schedule";

function formatSelectedDate(date: string, locale: Locale): string {
  const match = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return date;

  const [, year, month, day] = match;
  const parsed = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  return new Intl.DateTimeFormat(locale === "he" ? "he-IL" : "en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(parsed);
}

export function BookingDatePicker({ id, schedule, selectedDate, locale, now, disabled, onSelect }: {
  id: string;
  schedule: ScheduleResponse | null;
  selectedDate: string;
  locale: Locale;
  now: Date;
  disabled?: boolean;
  onSelect: (date: string) => void;
}) {
  const text = copy[locale];
  const range = getBookingDateWindow(now);
  const [invalid, setInvalid] = useState(false);
  const quickDates = (schedule?.days ?? [])
    .filter(day => isDateInWindow(calendarLabelToIso(day.label) ?? "", range))
    .sort((a, b) => (calendarLabelToIso(a.label) ?? "").localeCompare(calendarLabelToIso(b.label) ?? ""))
    .slice(0, 7);

  return (
    <div className="booking-date-picker">
      <label htmlFor={id} className="calendar-date-label">
        <CalendarDays size={23} aria-hidden="true" />
        <span>{text.chooseCalendarDate}</span>
      </label>
      <div className={`calendar-date-control${selectedDate ? "" : " empty"}${disabled ? " disabled" : ""}`}>
        <CalendarDays size={22} aria-hidden="true" />
        <span>{selectedDate ? formatSelectedDate(selectedDate, locale) : text.chooseCalendarDate}</span>
        <ChevronDown size={21} aria-hidden="true" />
        <input
          id={id}
          className="calendar-date-input"
          type="date"
          value={selectedDate}
          min={range.min}
          max={range.max}
          disabled={disabled}
          aria-label={text.chooseCalendarDate}
          aria-describedby={`${id}-hint${invalid ? ` ${id}-error` : ""}`}
          aria-invalid={invalid || undefined}
          onChange={event => {
            const date = event.currentTarget.value;
            if (date && !isDateInWindow(date, range)) { setInvalid(true); return; }
            setInvalid(false);
            onSelect(date);
          }}
        />
      </div>
      <p className="calendar-date-hint" id={`${id}-hint`}>
        {text.dateWindowHint.replace("{days}", String(BOOKING_DAYS_AHEAD)).replace("{date}", formatDay(isoToCalendarLabel(range.max), locale).date)}
      </p>
      {invalid && <p className="calendar-date-error" id={`${id}-error`} role="alert">{text.dateOutsideWindow.replace("{days}", String(BOOKING_DAYS_AHEAD))}</p>}
      {quickDates.length > 0 && (
        <>
          <p className="quick-dates-label">{text.nearbyDates}</p>
          <div className="date-strip" role="group" aria-label={text.nearbyDates}>
            {quickDates.map(day => {
              const iso = calendarLabelToIso(day.label)!;
              const formatted = formatDay(day.label, locale);
              const selected = iso === selectedDate;
              return (
                <button key={iso} type="button" aria-pressed={selected} disabled={disabled}
                  className={`date-pill${selected ? " selected" : ""}`}
                  onClick={() => { setInvalid(false); onSelect(iso); }}>
                  <span>{formatted.weekday}</span>
                  <strong>{formatted.date}</strong>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
